/**
 * @module services/tokenValidator
 * [Token validation]
 */

import { dateHelpers, storeHelpers } from 'utils'
import AuthActions, { thunks as AuthThunks } from 'reducers/Auth'
import {
  forceLogout,
  isSessionInvalidResponse
} from 'utils/authSession'

import { decodeToken } from 'utils/stringHelpers'
import ProfileService from './ProfileService'

// Token is considered expired if its expiry date is less than this value in the future (seconds)
const EXPIRY_THRESHOLD = 15

// Token will be refreshed if its expiry date is less than this value in the future (minutes)
const REFRESH_THRESHOLD = 5

export function checkTokenExpiry(token = null) {
  if (!token) return null
  const dToken = decodeToken(token)

  if (!dToken?.exp) {
    return {
      expired: true,
      shouldRefresh: false,
      invalid: true
    }
  }

  const expiry = dToken.exp * 10 ** (13 - `${dToken.exp}`.length)
  const now = Date.now()

  const expired = expiry < now + dateHelpers.seconds(EXPIRY_THRESHOLD)
  const shouldRefresh = expiry < now + dateHelpers.minutes(REFRESH_THRESHOLD)

  return { expired, shouldRefresh, invalid: false }
}

function handleExpiredToken() {
  console.warn('Token expired, logging out...')
  storeHelpers.asyncDispatch(AuthActions.openLogoutModal())
}

function handleTokenRefresh() {
  return storeHelpers.asyncDispatch(AuthThunks.refreshToken())
}

async function validateActiveSession() {
  try {
    await ProfileService.getProfile()
    return true
  } catch (error) {
    if (isSessionInvalidResponse(error)) {
      await forceLogout()
      return false
    }

    throw error
  }
}

export async function tokenValidator() {
  try {
    // Retrieve authentication state
    const authentication = await storeHelpers.asyncGetReducerState(
      'authentication'
    )

    // Ensure authentication and accessToken are available
    if (!authentication || !authentication.accessToken) {
      console.warn('[tokenValidator] No authentication or accessToken found')
      return false // Indicates that no valid token was found
    }

    // Check the token expiry
    const { expired, shouldRefresh } = checkTokenExpiry(
      authentication.accessToken
    )

    if (shouldRefresh) {
      return await handleTokenRefresh()
    } else if (expired) {
      handleExpiredToken()
      return true
    }

    return await validateActiveSession()
  } catch (e) {
    // Set loading to false and log the error
    AuthActions.setLoading(false)
    console.error('[tokenValidator] Token validation failed:', e)
    return false // Indicates an error occurred
  }
}

export default tokenValidator
